import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Eyebrow, SectionLabel } from "@/components/Eyebrow";

const SECTIONS = [
  { h: "The UF reality", b: "Admissions decisions are made by individual colleges and departments. Meeting university minimums does not mean meeting departmental competitiveness." },
  { h: "The one-major policy", b: "Students apply to exactly one major. Changing majors after admission is highly unlikely. The major you apply with is, in practice, the major you graduate with." },
  { h: "Prerequisites must be completed before enrollment", b: "Not after. UF reads the application against the Common Prerequisites Manual entry for your specific major. Missing prereqs are not deferred — they are disqualifying." },
  { h: "Limited-access majors", b: "What they are, how to recognize them in the Common Prerequisites Manual, and how their competitive bands compare to the university's stated minimums." },
  { h: "Timeline", b: "When to apply (spring/summer cycle for fall entry), what's due when, how the rolling read actually works." },
  { h: "The UF Transfer Viability Memo", b: "The firm's signature UF-track deliverable. A written, per-student assessment of competitiveness for the intended major, with prerequisite gap analysis and a semester-by-semester remediation plan." },
];

export default function PlaybookUF() {
  return (
    <Layout>
      <SEO
        title="UF Transfer Playbook — Departmental Review, Prereqs, One-Major Policy"
        description="The University of Florida is limited access, departmental review, one-major policy. Prerequisites must be complete before enrollment. Read the playbook."
        canonical="/playbooks/uf"
        keywords="UF transfer, University of Florida transfer, UF prerequisites, UF one-major policy, UF limited access, Forbes New Ivy"
      />
      <section className="border-b border-stone/30 bg-bone" data-testid="playbook-uf-page">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-20 lg:pt-28 pb-12">
          <div className="mb-6 border-l-2 border-brass pl-4 py-2 text-xs text-ink-soft" data-testid="playbook-uf-fl-callout">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">Florida Specialization Playbook</div>
            <div className="mt-1">Part of the Beyond Valencia Florida transfer engine. For the current primary flow, see the <a href="/mishap-corrector" className="text-ivy hover:text-brass underline">Mishap Corrector</a> or a <a href="/transfer-guides/valencia-college/uf-business" className="text-ivy hover:text-brass underline">pair-page transfer guide</a>.</div>
          </div>
          <Eyebrow>Florida Specialization Playbook · UF Transfers</Eyebrow>
          <h1 className="font-serif font-medium text-4xl sm:text-5xl lg:text-[4.75rem] text-ink leading-[1.04] tracking-tightest mt-5">
            University of Florida<br /><span className="italic text-ivy">Transfers.</span>
          </h1>
          <p className="mt-6 text-ink-soft text-base sm:text-lg max-w-2xl leading-relaxed">
            Limited access. Departmental review. One-major policy. Prereqs completed before enrollment.
          </p>

          <div className="mt-10 border-l-2 border-brass bg-bone-dim p-7" data-testid="uf-forbes-callout">
            <div className="text-[11px] uppercase tracking-[0.22em] text-brass font-semibold">2026 Forbes Public New Ivy</div>
            <p className="mt-3 font-serif font-medium text-xl sm:text-2xl text-ink leading-snug tracking-tight">
              &ldquo;UF is a 2026 Forbes Public New Ivy.&rdquo;
            </p>
            <p className="mt-3 text-sm text-ink-soft leading-relaxed">
              The same Forbes list that ranks Georgia Tech, Michigan, UNC Chapel Hill, UT Austin, and UVA. Your Florida AA articulates straight in.
            </p>
            <a href="/resources/florida-aa-to-forbes-new-ivy" className="mt-4 inline-block text-[11px] uppercase tracking-[0.22em] font-semibold text-ivy hover:text-brass">Read the full New Ivy guide →</a>
          </div>
        </div>
      </section>

      <article className="max-w-3xl mx-auto px-5 sm:px-8 py-20 lg:py-24 space-y-12">
        {SECTIONS.map((s, i) => (
          <div key={s.h} className="border-l-2 border-brass pl-5">
            <SectionLabel index={String(i + 1).padStart(2, "0")} label={s.h} />
            <p className="mt-4 text-ink-soft leading-relaxed">{s.b}</p>
          </div>
        ))}
      </article>

      <section className="bg-ivy text-bone py-20 text-center">
        <p className="font-serif font-medium text-2xl sm:text-3xl tracking-tight px-5">Order a Transfer Viability Memo.</p>
        <a href="/transfer-audit" className="inline-block mt-8 bg-brass hover:bg-brass-soft text-ink uppercase tracking-[0.18em] text-xs font-semibold py-4 px-8 transition-colors">Schedule Audit — $249</a>
      </section>
    </Layout>
  );
}
